package com.merkost.handpass.admin.container

import com.merkost.handpass.admin.api.AdminRepositoryApi
import com.merkost.handpass.admin.contract.AsyncSection
import com.merkost.handpass.admin.contract.ListingsAction
import com.merkost.handpass.admin.contract.ListingsIntent
import com.merkost.handpass.admin.contract.ListingsState
import com.merkost.handpass.admin.platform.WindowFocusTrigger
import com.merkost.handpass.admin.util.toAdminMessage
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.launch
import pro.respawn.flowmvi.api.Container
import pro.respawn.flowmvi.api.PipelineContext
import pro.respawn.flowmvi.dsl.lazyStore
import pro.respawn.flowmvi.plugins.recover
import pro.respawn.flowmvi.plugins.reduce

@OptIn(FlowPreview::class)
class ListingsContainer(
    private val repo: AdminRepositoryApi,
    private val focusTrigger: WindowFocusTrigger,
) : Container<ListingsState, ListingsIntent, ListingsAction> {

    private val scope = MainScope()

    private val searchFlow = MutableSharedFlow<String>(replay = 1)

    override val store by lazyStore(ListingsState(), scope) {
        configure { name = "ListingsStore" }
        recover { e ->
            updateState { copy(data = AsyncSection.Failed(e.toAdminMessage())) }
            null
        }
        reduce { intent ->
            when (intent) {
                is ListingsIntent.SetStatusFilter -> {
                    updateState { copy(statusFilter = intent.value, page = 1) }
                    reload()
                }
                is ListingsIntent.SetSearch -> {
                    updateState { copy(search = intent.value) }
                    searchFlow.tryEmit(intent.value)
                }
                is ListingsIntent.GoToPage -> {
                    updateState { copy(page = intent.page) }
                    reload()
                }
                is ListingsIntent.TapListing -> {
                    action(ListingsAction.Navigate("/listings/${intent.id}"))
                }
                is ListingsIntent.ReactToFocusTick, ListingsIntent.Refresh -> reload()
                is ListingsIntent.SetSort -> {
                    updateState { copy(sortBy = intent.sortBy, sortAsc = intent.sortAsc, page = 1) }
                    reload()
                }
            }
        }
    }

    init {
        scope.launch {
            focusTrigger.ticks.collect { tick -> store.intent(ListingsIntent.ReactToFocusTick(tick)) }
        }
        scope.launch {
            searchFlow.debounce(300).distinctUntilChanged().collect { store.intent(ListingsIntent.GoToPage(1)) }
        }
    }

    private suspend fun PipelineContext<ListingsState, ListingsIntent, ListingsAction>.currentState(): ListingsState {
        var s = ListingsState()
        withState { s = this }
        return s
    }

    private suspend fun PipelineContext<ListingsState, ListingsIntent, ListingsAction>.reload() {
        val s = currentState()
        val statusParam = if (s.statusFilter == "All") null else s.statusFilter
        updateState { copy(data = AsyncSection.Loading) }
        val resp = repo.getListings(page = s.page, pageSize = 20, search = s.search.ifBlank { null }, status = statusParam, sellerId = null, sortBy = s.sortBy, sortAsc = s.sortAsc)
        updateState {
            copy(
                data = AsyncSection.Loaded(resp.data),
                totalPages = resp.meta?.totalPages ?: 1,
                totalCount = resp.meta?.totalCount ?: 0L,
            )
        }
    }
}
